import type { DmPrinterState, DmPrinterStatus } from '@nsftx/systems-sdk'
import type { PrinterStatus } from '#src/main'
import { PrinterStatusCode, PrinterStatusSeverity } from '#src/types/PrinterStatusTypes'

const PRINTER_DAMAGED_STATE = {
  paperLow: 'Paper low',
  paperOut: 'Paper out'
} as const

const getPrinterDamagedState = (statuses: DmPrinterState[], canPrint: boolean) => {
  let damagedState: DmPrinterState | undefined

  if (!canPrint) {
    damagedState = statuses.find(
      ({ status, severity }) =>
        status === PrinterStatusCode.Error && severity === PrinterStatusSeverity.Error
    )
    return damagedState
  }

  damagedState = statuses.find(
    ({ status, severity }) =>
      status === PrinterStatusCode.Error && severity === PrinterStatusSeverity.Error
  )
  if (!damagedState) {
    damagedState = statuses.find(
      ({ status, severity }) =>
        status === PrinterStatusCode.Error && severity === PrinterStatusSeverity.Warning
    )
  }

  return damagedState
}

const getPrinterDamagedStateMessageKey = (damagedStatusName: string) => {
  /**
   * empty spaces and special characters are replaced with underscores
   */
  const damagedStatusNameKey = damagedStatusName
    .toLowerCase()
    .replace(/\s/g, '_')
    .replace(/[^\w\s]/gi, '_')

  return `printer_${damagedStatusNameKey}`
}

const getPrinterStatusByDamagedState = (statuses: DmPrinterState[], canPrint: boolean) => {
  const printerStatus: PrinterStatus = {
    valid: false,
    code: 'undefined',
    message: '',
    paperLow: false,
    paperOut: false
  }
  const damagedStatus = statuses ? getPrinterDamagedState(statuses, canPrint) : null

  if (!damagedStatus) {
    return printerStatus
  }

  const { name: damagedStateName, severity } = damagedStatus
  printerStatus.code =
    severity === PrinterStatusSeverity.Error ? PrinterStatusCode.Error : PrinterStatusCode.Warning
  printerStatus.valid = severity !== PrinterStatusSeverity.Error
  printerStatus.message = getPrinterDamagedStateMessageKey(damagedStateName)
  printerStatus.paperLow = damagedStateName === PRINTER_DAMAGED_STATE.paperLow
  printerStatus.paperOut = damagedStateName === PRINTER_DAMAGED_STATE.paperOut

  return printerStatus
}

const parsePrinterStatusResponse = (response: DmPrinterStatus) => {
  const { status: printerStatuses } = response
  let printerStatus: PrinterStatus = {
    valid: false,
    code: 'undefined',
    message: '',
    paperLow: false,
    paperOut: false
  }

  if (!printerStatuses?.length) {
    if (!response.can_print) {
      printerStatus.code = PrinterStatusCode.Error
      printerStatus.message = 'printer_offline'
      return printerStatus
    }

    printerStatus.code = PrinterStatusCode.Ok
    printerStatus.valid = true
    return printerStatus
  }

  printerStatus = getPrinterStatusByDamagedState(printerStatuses, response.can_print)
  if (printerStatus.code !== PrinterStatusCode.Undefined) {
    return printerStatus
  }

  printerStatus.code = PrinterStatusCode.Ok
  printerStatus.valid = true

  return printerStatus
}

export { parsePrinterStatusResponse }
